import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { toast } from 'sonner';
import { X, Loader2, ScanLine, Trash2, Printer, FileSpreadsheet, Check, Save, Volume2, VolumeX, ChevronLeft, CheckCircle2, Images, FileDown, Share2 } from 'lucide-react';
import { buildImageListPdf } from '@/lib/tagImageList';
import { rateCardById } from '@/lib/rateCards';
import { PricingControls, getPricing, ensureRates, pricingOn, stonesFor, addStoneRates, applyListStones } from '@/lib/tagPricing';
import { notesApi } from '@/lib/notesApi';
import { extractError } from '@/lib/errors';
import { buildTagDataFromDetail, tagMaterials } from '@/components/jewellery/JewelleryTagViewer';
import { downloadTagsExcel } from '@/lib/tagExcel';
import { tagThemeClass } from './tagTheme';
import { askSummary } from './TagSummaryAsk';
import { JewelleryMultiPicker } from './JewelleryMultiPicker';
import { SalesPicker } from '@/lib/salesPersons';
import { usePickBrands, BrandPick } from './TagBrandBits';

const LIMIT = 30;
const REGION = 'tagws-scan-region';
// Short beep (+ vibration) on a good read; low double-beep on a duplicate / miss.
function feedback(ok) {
  if (localStorage.getItem('tagws_scan_mute') === '1') return;
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    (ok ? [[0, 1200]] : [[0, 300], [0.18, 300]]).forEach(([t, hz]) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.frequency.value = hz; g.gain.value = 0.15; o.connect(g); g.connect(ctx.destination);
      o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + 0.12);
    });
    setTimeout(() => ctx.close(), 600);
  } catch { /* no audio */ }
  try { navigator.vibrate?.(ok ? 80 : [60, 60, 60]); } catch { /* no vibration */ }
}

const idFromQr = (text) => (String(text).match(/\/t\/([\w-]+)/) || [])[1] || String(text).trim();
const fetchTags = async (ids) => (await notesApi.post('/notes/tag-workspace/tags/batch', { jewellery_ids: ids })).data.items || [];

const SEG = (on) => `flex-1 px-2 py-1.5 text-xs font-medium transition-colors ${on ? 'bg-primary text-primary-foreground' : 'bg-card text-foreground'}`;

function FormatPicker({ fmt, setFmt }) {
  const seg = (k, v, label) => (
    <button type="button" key={v} onClick={() => setFmt({ ...fmt, [k]: v })} aria-pressed={fmt[k] === v} className={SEG(fmt[k] === v)} data-testid={`tagws-scan-format-${k}-${v}`}>{label}</button>
  );
  return (
    <div className="space-y-1.5" data-testid="tagws-scan-format">
      <div className="text-[length:calc(var(--fs,1)*0.6875rem)] font-semibold uppercase tracking-wide text-muted-foreground">Show tags as</div>
      <div className="flex overflow-hidden rounded-lg border border-border">{[['expanded', 'Expanded'], ['collapsed', 'Collapsed'], ['raw', 'Raw']].map(([v, l]) => seg('rmMode', v, l))}</div>
      <div className="flex overflow-hidden rounded-lg border border-border">{[[true, 'With pieces'], [false, 'Without pieces']].map(([v, l]) => seg('showPieces', v, l))}</div>
    </div>
  );
}

function ConfirmCard({ pending, onAdd, onSkip }) {
  if (!pending) return null;
  return (
    <div className="rounded-xl border border-[hsl(var(--ring))] bg-[hsl(var(--accent))] p-3" data-testid="tagws-scan-confirm">
      {pending.loading ? <div className="flex items-center gap-2 text-sm"><Loader2 size={16} className="animate-spin" /> Looking up the tag…</div> : (
        <div className="flex items-center gap-3">
          {pending.image ? <img src={pending.image} alt="" className="h-12 w-12 rounded-lg object-cover" /> : null}
          <div className="min-w-0 flex-1">
            <div className="text-xs text-muted-foreground">Add this jewellery?</div>
            <div className="truncate font-mono text-base font-bold text-foreground" data-testid="tagws-scan-confirm-number">{pending.jewelleryNo}</div>
          </div>
          <button onClick={onSkip} className="h-10 rounded-lg border border-border bg-card px-3 text-sm" data-testid="tagws-scan-skip-button">Skip</button>
          <button onClick={onAdd} className="inline-flex h-10 items-center gap-1 rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground" data-testid="tagws-scan-add-button"><Check size={15} /> Add</button>
        </div>
      )}
    </div>
  );
}

// Mobile multi-scan: scan QR tags one after another, confirm each, then print or export.
export function TagScanDialog({ open, onClose, onViewTags, editList, onSaved, items = [] }) {
  const qrRef = useRef(null);
  const busyRef = useRef(false);
  const listRef = useRef([]);
  const [list, setList] = useState([]); // [{ id, jewelleryNo, image }]
  const [pending, setPending] = useState(null);
  const [camError, setCamError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [fmt, setFmt] = useState({ rmMode: 'expanded', showPieces: true });
  const [saveName, setSaveName] = useState('');
  const [savedId, setSavedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sales, setSales] = useState([]);
  const pickBrands = usePickBrands();
  const [brandId, setBrandId] = useState('');
  useEffect(() => {
    if (!open) return;
    setList((editList?.items || []).map((x) => ({ id: x.id, jewelleryNo: x.jewellery_no })));
    setFmt(editList ? { rmMode: editList.rm_mode, showPieces: editList.show_pieces } : { rmMode: 'expanded', showPieces: true });
    setSaveName(editList?.name || '');
    setSavedId(editList?.id || null);
    setSales(editList?.sales_ids || []);
    setBrandId(editList?.brand_id || '');
    if (editList) applyListStones(editList.stone_rates);
    setPending(null);
  }, [open, editList]);
  const save = async () => {
    if (!saveName.trim()) { toast.error('Give the list a name.'); return; }
    if (pickBrands.length && !brandId) { toast.error('Choose the brand.'); return; }
    setSaving(true);
    const body = { name: saveName.trim(), items: list.map((x) => ({ id: x.id, jewellery_no: x.jewelleryNo })), rm_mode: fmt.rmMode, show_pieces: fmt.showPieces, rate_card_id: editList?.rate_card_id || '', sales_ids: sales, stone_rates: { ...(editList?.stone_rates || {}), ...stonesFor(list.map((x) => x.id)) }, brand_id: brandId };
    try {
      const { data } = savedId ? await notesApi.put(`/notes/tag-workspace/tag-lists/${savedId}`, body) : await notesApi.post('/notes/tag-workspace/tag-lists', body);
      setSavedId(data.id); toast.success(`List "${data.name}" saved.`); onSaved?.(data);
    } catch (e) { toast.error(extractError(e, 'Could not save the list.')); } finally { setSaving(false); }
  };
  listRef.current = list;

  const [mute, setMute] = useState(localStorage.getItem('tagws_scan_mute') === '1');
  const toggleMute = () => { const m = !mute; setMute(m); localStorage.setItem('tagws_scan_mute', m ? '1' : '0'); };
  const resume = () => { busyRef.current = false; try { qrRef.current?.resume(); } catch { /* not paused */ } };
  const onScan = useCallback(async (text) => {
    if (busyRef.current) return;
    const id = idFromQr(text);
    if (listRef.current.some((x) => x.id === id)) {
      if (!busyRef.current && Date.now() - (window.__tagwsDupAt || 0) > 2500) { window.__tagwsDupAt = Date.now(); feedback(false); toast.info('Already in the list.'); }
      return;
    }
    busyRef.current = true;
    try { qrRef.current?.pause(true); } catch { /* ignore */ }
    if (listRef.current.length >= LIMIT) { toast.error(`You can add up to ${LIMIT} tags.`); setTimeout(resume, 1200); return; }
    setPending({ loading: true, id });
    try {
      const it = (await fetchTags([id]))[0];
      if (!it) { feedback(false); toast.error('This tag is not in your workspace.'); setPending(null); setTimeout(resume, 1200); return; }
      feedback(true);
      setPending({ id, jewelleryNo: it.jewellery_no, image: it.image });
    } catch (e) {
      toast.error(extractError(e, 'Could not read this tag.')); setPending(null); setTimeout(resume, 1200);
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    window.dispatchEvent(new CustomEvent('tagws-scan-open', { detail: true }));
    return () => window.dispatchEvent(new CustomEvent('tagws-scan-open', { detail: false }));
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    setCamError('');
    const qr = new Html5Qrcode(REGION);
    qrRef.current = qr;
    qr.start({ facingMode: 'environment' }, { fps: 10, qrbox: { width: 220, height: 220 } }, onScan, () => {})
      .catch(() => setCamError('Camera not available. Allow camera access for this site and try again.'));
    return () => { qr.stop().catch(() => {}).finally(() => { try { qr.clear(); } catch { /* ignore */ } }); qrRef.current = null; };
  }, [open, onScan]);

  const typeIn = (picked) => {
    const room = LIMIT - listRef.current.length;
    if (room <= 0) { toast.error(`You can add up to ${LIMIT} tags.`); return; }
    const take = picked.slice(0, room);
    if (picked.length > room) toast.error(`You can add up to ${LIMIT} tags — added the first ${room}.`);
    feedback(true);
    setList((l) => [...l, ...take.filter((it) => !l.some((x) => x.id === it.id)).map((it) => ({ id: it.id, jewelleryNo: it.jewellery_no, image: it.image || it.fallback_image }))]);
    toast.success(take.length === 1 ? `${take[0].jewellery_no} added.` : `${take.length} pieces added.`);
  };
  const add = () => { setList((l) => [...l, { id: pending.id, jewelleryNo: pending.jewelleryNo, image: pending.image }]); toast.success(`${pending.jewelleryNo} added.`); setPending(null); resume(); };
  const skip = () => { setPending(null); resume(); };
  const excel = async () => {
    const built = (await fetchTags(list.map((x) => x.id))).map(buildTagDataFromDetail);
    if (!(await ensureRates(built, 'page', stonePersist))) return;
    await downloadTagsExcel(built.map((d) => ({ data: d, materials: tagMaterials(d, fmt.rmMode) })), { ...fmt, withRates: pricingOn(), rates: rateCardById(getPricing().cardId), listName: savedId ? saveName.trim() : '' });
  };
  const [phase, setPhase] = useState('scan');
  useEffect(() => { if (open) setPhase('scan'); }, [open, editList]);
  const finishing = phase === 'finish';
  useEffect(() => {
    try { if (finishing) qrRef.current?.pause(true); else if (!pending) qrRef.current?.resume(); } catch { /* not running */ }
  }, [finishing]); // eslint-disable-line react-hooks/exhaustive-deps
  const name = savedId ? saveName.trim() : '';
  // Per-piece stone rates typed here are kept on the saved list.
  const stonePersist = async (sr) => {
    if (!savedId) return;
    try { await notesApi.put(`/notes/tag-workspace/tag-lists/${savedId}`, { name: saveName.trim(), items: list.map((x) => ({ id: x.id, jewellery_no: x.jewelleryNo })), rm_mode: fmt.rmMode, show_pieces: fmt.showPieces, rate_card_id: editList?.rate_card_id || '', sales_ids: sales, stone_rates: stonesFor(list.map((x) => x.id)) }); } catch { /* kept for this session */ }
  };
  const run = async (key, fn) => { setExporting(key); try { await fn(); } catch (e) { toast.error(extractError(e, 'Something went wrong.')); } finally { setExporting(false); } };
  const images = (action) => run(action, async () => {
    const summary = await askSummary();
    if (summary == null) return;
    const built = (await fetchTags(list.map((x) => x.id))).map(buildTagDataFromDetail);
    if (!(await ensureRates(built, 'page', stonePersist))) return;
    const pdf = await buildImageListPdf(built, fmt.rmMode, name || 'Scanned list', summary);
    if (action === 'pdf') { pdf.save(`${name || 'Scanned list'} - images.pdf`); return; }
    pdf.autoPrint();
    if (!window.open(pdf.output('bloburl'), '_blank')) toast.error('Please allow pop-ups to print.');
  });
  const share = () => run('share', async () => {
    if (!savedId) { toast.error('Save the list first, then share it.'); return; }
    const url = `${window.location.origin}/l/${(await notesApi.post(`/notes/tag-workspace/tag-lists/${savedId}/share`)).data.token}`;
    onSaved?.();
    if (navigator.share) { try { await navigator.share({ title: name, url }); } catch { /* cancelled */ } return; }
    try { await navigator.clipboard.writeText(url); toast.success('Link copied — valid for 7 days.'); } catch { toast.info(url); }
  });
  if (!open) return null;
  return (
    <div className={`tag-scope ${tagThemeClass()} fixed inset-0 z-[250] flex flex-col bg-background`} data-testid="tagws-scan-dialog">
      <div className="flex items-center justify-between border-b border-border bg-card/80 px-4 py-3 backdrop-blur">
        <div className="flex min-w-0 items-center gap-2.5">
          {finishing ? (
            <button onClick={() => setPhase('scan')} aria-label="Back to scanning" className="grid h-9 w-9 place-items-center rounded-full bg-secondary text-foreground" data-testid="tagws-scan-back-button"><ChevronLeft size={18} /></button>
          ) : <span className="grid h-9 w-9 place-items-center rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--ring))]"><ScanLine size={18} /></span>}
          <div className="min-w-0">
            <div className="truncate text-[length:calc(var(--fs,1)*0.9375rem)] font-semibold leading-tight text-foreground">{finishing ? 'Your list is ready' : editList ? editList.name : 'Scan to build a list'}</div>
            <div className="text-[length:calc(var(--fs,1)*0.6875rem)] text-muted-foreground"><span className="font-mono font-semibold text-foreground" data-testid="tagws-scan-count">{list.length}/{LIMIT}</span> pieces{finishing ? ' · choose what to do' : ' · keep scanning'}</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {!finishing ? <button onClick={toggleMute} aria-label={mute ? 'Unmute' : 'Mute'} className="grid h-9 w-9 place-items-center rounded-full text-muted-foreground hover:bg-secondary" data-testid="tagws-scan-mute-button">{mute ? <VolumeX size={18} /> : <Volume2 size={18} />}</button> : null}
          <button onClick={onClose} aria-label="Close" className="grid h-9 w-9 place-items-center rounded-full text-muted-foreground hover:bg-secondary" data-testid="tagws-scan-close-button"><X size={20} /></button>
        </div>
      </div>
      <div className={`flex-1 space-y-3 overflow-y-auto p-4 ${finishing ? 'hidden' : ''}`}>
        <div className="relative mx-auto w-full max-w-sm overflow-hidden rounded-2xl bg-black shadow-lg ring-1 ring-black/10">
          <div id={REGION} className="w-full" data-testid="tagws-scan-camera" />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-3 pb-2 pt-6 text-center text-[length:calc(var(--fs,1)*0.6875rem)] font-medium text-white/90">Point at a tag&apos;s QR code · each scan asks to confirm</div>
        </div>
        {camError ? <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="tagws-scan-camera-error">{camError}</div> : null}
        <ConfirmCard pending={pending} onAdd={add} onSkip={skip} />
        <JewelleryMultiPicker items={items} excludeIds={list.map((x) => x.id)} onAdd={typeIn} placeholder="Or type jewellery no." testid="tagws-scan-typein" />
        <div className="space-y-1.5" data-testid="tagws-scan-list">
          {list.length ? <div className="px-1 text-[length:calc(var(--fs,1)*0.6875rem)] font-semibold uppercase tracking-wide text-muted-foreground">In this list</div> : null}
          {list.map((x, i) => (
            <div key={x.id} className="flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2 shadow-sm" data-testid="tagws-scan-list-item">
              <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[hsl(var(--accent))] text-[length:calc(var(--fs,1)*0.6875rem)] font-semibold text-foreground">{i + 1}</span>
              {x.image ? <img src={x.image} alt="" className="h-9 w-9 rounded-lg object-cover" /> : null}
              <span className="flex-1 truncate font-mono text-sm font-semibold">{x.jewelleryNo}</span>
              <button onClick={() => setList((l) => l.filter((y) => y.id !== x.id))} aria-label="Remove" className="grid h-8 w-8 place-items-center rounded-full text-muted-foreground hover:bg-secondary hover:text-destructive" data-testid="tagws-scan-remove-button"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      </div>
      {finishing ? (
        <div className="flex-1 space-y-4 overflow-y-auto p-4" data-testid="tagws-scan-finish">
          <div className="flex flex-wrap gap-1.5" data-testid="tagws-scan-finish-chips">
            {list.map((x) => <span key={x.id} className="rounded-full border border-border bg-card px-2.5 py-1 font-mono text-[length:calc(var(--fs,1)*0.6875rem)] font-semibold text-foreground">{x.jewelleryNo}</span>)}
          </div>
          <section className="space-y-2 rounded-2xl border border-border bg-card p-3 shadow-sm">
            <div className="text-[length:calc(var(--fs,1)*0.6875rem)] font-semibold uppercase tracking-wide text-muted-foreground">Options</div>
            <FormatPicker fmt={fmt} setFmt={setFmt} />
            <PricingControls testid="tagws-scan-pricing" />
          </section>
          <section className="space-y-2 rounded-2xl border border-border bg-card p-3 shadow-sm">
            <div className="text-[length:calc(var(--fs,1)*0.6875rem)] font-semibold uppercase tracking-wide text-muted-foreground">Save for later {savedId ? <span className="ml-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[length:calc(var(--fs,1)*0.625rem)] text-emerald-800" data-testid="tagws-scan-saved-badge">Saved</span> : null}</div>
            <input value={saveName} onChange={(e) => setSaveName(e.target.value)} maxLength={80} placeholder="List name (e.g. Mehta – 19 Oct)" className="h-11 w-full rounded-xl border border-input bg-background px-3 text-sm" data-testid="tagws-scan-list-name-input" />
            <BrandPick brands={pickBrands} value={brandId} onChange={setBrandId} testid="tagws-scan-brand" />
            <SalesPicker value={sales} onChange={setSales} testid="tagws-scan-sales" />
            <button onClick={save} disabled={saving} className="inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-xl bg-primary text-sm font-semibold text-primary-foreground disabled:opacity-50" data-testid="tagws-scan-save-list-button">{saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {savedId ? 'Update saved list' : 'Save list'}</button>
          </section>
          <section className="grid grid-cols-2 gap-2" data-testid="tagws-scan-actions">
            <ActionTile icon={Printer} label="View & print tags" onClick={() => onViewTags(list.map((x) => x.id), { ...fmt, listName: name })} testid="tagws-scan-view-button" />
            <ActionTile icon={FileSpreadsheet} label="Download Excel" busy={exporting === 'excel'} onClick={() => run('excel', excel)} testid="tagws-scan-excel-button" />
            <ActionTile icon={Images} label="Print image list" busy={exporting === 'print'} onClick={() => images('print')} testid="tagws-scan-image-print" />
            <ActionTile icon={FileDown} label="Image list PDF" busy={exporting === 'pdf'} onClick={() => images('pdf')} testid="tagws-scan-image-pdf" />
            <ActionTile icon={Share2} label={savedId ? 'Share link' : 'Share (save first)'} busy={exporting === 'share'} onClick={share} testid="tagws-scan-share-button" wide />
          </section>
        </div>
      ) : (
        <div className="border-t border-border bg-card/90 p-3 backdrop-blur" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
          <button disabled={!list.length} onClick={() => setPhase('finish')} className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-primary text-[length:calc(var(--fs,1)*0.9375rem)] font-semibold text-primary-foreground shadow-lg shadow-primary/20 disabled:opacity-40" data-testid="tagws-scan-done-button">
            <CheckCircle2 size={18} /> {list.length ? `Done scanning · ${list.length} piece${list.length === 1 ? '' : 's'}` : 'Scan a tag to start'}
          </button>
        </div>
      )}
    </div>
  );
}

function ActionTile({ icon: Icon, label, onClick, busy, testid, wide }) {
  return (
    <button onClick={onClick} disabled={busy} className={`flex h-20 flex-col items-center justify-center gap-1.5 rounded-2xl border border-border bg-card text-sm font-semibold text-foreground shadow-sm transition-transform active:scale-[0.97] disabled:opacity-50 ${wide ? 'col-span-2 h-14 flex-row' : ''}`} data-testid={testid}>
      {busy ? <Loader2 size={20} className="animate-spin" /> : <Icon size={20} className="text-[hsl(var(--ring))]" />} {label}
    </button>
  );
}
